import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import Button from '../ui/Button';
import { StatusPill } from '../ui/Status';
import DataTable from '../ui/DataTable';
import StatusBanner from '../ui/StatusBanner';
import DirectorModal from './DirectorModal';
import DirectorPanelState from './DirectorPanelState';
import PerformanceContextBar from './PerformanceContextBar';
import PerformanceStatusSummaryStrip from './PerformanceStatusSummaryStrip';
import {
  buildActualScorecardRows,
  createDefaultPerformanceContext,
  derivePeriodStartFromContext,
  formatMetricValue,
  getPerformanceRowStatus,
  normalizePeriodStartValue
} from './performanceRows';
import {
  DIRECTOR_DATA_UPDATED_EVENT,
  notifyDirectorAdminToast,
  notifyDirectorDataUpdated
} from '../../utils/directorEvents';

const ALL_DIRECTOR_LOCATIONS = 'ALL';
const EMPTY_ROW_ACTION = { type: '', row: null };
const EMPTY_ALLOCATION_DRAFT = {
  allocationType: '',
  managerIds: []
};

function isGlobalDirectorManager(manager) {
  return String(manager?.managerRoleKey || '').toLowerCase() === 'director';
}

function sortGoals(goalRows) {
  return [...goalRows].sort((a, b) => {
    const aKey = `${a.goalYear}-${String(a.goalQuarter || 0).padStart(2, '0')}-${String(a.goalMonth || 0).padStart(2, '0')}-${String(a.goalWeek || 0).padStart(2, '0')}-${a.locationCode}-${a.kpiKey}`;
    const bKey = `${b.goalYear}-${String(b.goalQuarter || 0).padStart(2, '0')}-${String(b.goalMonth || 0).padStart(2, '0')}-${String(b.goalWeek || 0).padStart(2, '0')}-${b.locationCode}-${b.kpiKey}`;
    return bKey.localeCompare(aKey);
  });
}

function sortActuals(actualRows) {
  return [...actualRows].sort((a, b) => {
    const aKey = `${a.actualYear}-${String(a.actualQuarter || 0).padStart(2, '0')}-${String(a.actualMonth || 0).padStart(2, '0')}-${String(a.actualWeek || 0).padStart(2, '0')}-${a.managerName}-${a.kpiKey}`;
    const bKey = `${b.actualYear}-${String(b.actualQuarter || 0).padStart(2, '0')}-${String(b.actualMonth || 0).padStart(2, '0')}-${String(b.actualWeek || 0).padStart(2, '0')}-${b.managerName}-${b.kpiKey}`;
    return bKey.localeCompare(aKey);
  });
}

function createAllocationDraftFromRows(rows = []) {
  if (!rows.length) {
    return { ...EMPTY_ALLOCATION_DRAFT };
  }

  return {
    allocationType: rows[0]?.allocationType || '',
    managerIds: rows
      .map((row) => row.managerId)
      .filter((value) => Number.isFinite(value))
  };
}

function getWorkspaceCopy(periodType) {
  if (periodType === 'yearly') {
    return {
      title: 'Goals & Performance',
      description: 'Manage yearly goals, manager allocations, actuals, and variance in one scorecard workspace.',
      targetColumn: 'Yearly Goal',
      actualColumn: 'YTD Actual'
    };
  }

  return {
    title: 'Goals & Performance',
    description: 'Manage monthly goals, allocations, actuals, and variance in one scorecard workspace.',
    targetColumn: 'Monthly Goal',
    actualColumn: 'MTD Actual'
  };
}

function getPeriodScopeLabel(context = {}) {
  if (context.periodType === 'yearly') {
    return `Yearly ${context.year || ''}`.trim();
  }

  if (context.periodType === 'monthly') {
    return `Monthly ${context.month || ''}/${context.year || ''}`.trim();
  }

  if (context.periodType === 'quarterly') {
    return `Quarterly Q${context.quarter || ''} ${context.year || ''}`.trim();
  }

  return `Weekly ${context.week || ''} ${context.year || ''}`.trim();
}

function formatAllocationTimestamp(value) {
  if (!value) {
    return 'n/a';
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'n/a' : parsed.toLocaleString();
}

function DirectorGoalsPerformancePanel({
  selectedLocationCode = ALL_DIRECTOR_LOCATIONS,
  locationLabel = 'All Assigned Sites',
  performanceContext,
  onPerformanceContextChange,
  selectedManagerIdExternal = '',
  onSelectedManagerChange,
  selectedKpiKey = '',
  onSelectKpi,
  onUpdateSelectedKpiSnapshot,
  defaultStage = 'goals'
}) {
  const context = performanceContext || createDefaultPerformanceContext();
  const [locations, setLocations] = useState([]);
  const [managers, setManagers] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [goals, setGoals] = useState([]);
  const [actuals, setActuals] = useState([]);
  const [allocationsByGoalId, setAllocationsByGoalId] = useState({});
  const [search, setSearch] = useState('');
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [rowAction, setRowAction] = useState(EMPTY_ROW_ACTION);
  const [goalDraftValue, setGoalDraftValue] = useState('');
  const [goalHistory, setGoalHistory] = useState([]);
  const [allocationDraft, setAllocationDraft] = useState(EMPTY_ALLOCATION_DRAFT);
  const [actualDraftValue, setActualDraftValue] = useState('');
  const [actualHistory, setActualHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [banner, setBanner] = useState('');
  const [error, setError] = useState('');
  const [actualModalError, setActualModalError] = useState('');
  const effectiveLocationCode = selectedLocationCode !== ALL_DIRECTOR_LOCATIONS ? selectedLocationCode : '';
  const resolvedLocationCode = effectiveLocationCode
    || (context.locationCode && context.locationCode !== ALL_DIRECTOR_LOCATIONS ? context.locationCode : '');
  const periodStart = useMemo(() => derivePeriodStartFromContext(context), [context]);
  const workspaceCopy = useMemo(() => getWorkspaceCopy(context.periodType), [context.periodType]);

  const scopedLocations = useMemo(
    () => (effectiveLocationCode
      ? locations.filter((location) => location.isActive && location.code === effectiveLocationCode)
      : locations.filter((location) => location.isActive)),
    [effectiveLocationCode, locations]
  );

  const scopedManagers = useMemo(
    () => (effectiveLocationCode
      ? managers.filter((manager) => manager.locationCode === effectiveLocationCode || isGlobalDirectorManager(manager))
      : managers.filter((manager) => (
        !context.locationCode
        || manager.locationCode === context.locationCode
        || isGlobalDirectorManager(manager)
      ))),
    [context.locationCode, effectiveLocationCode, managers]
  );

  const selectedManager = useMemo(
    () => scopedManagers.find((manager) => String(manager.id) === String(selectedManagerId)) || null,
    [scopedManagers, selectedManagerId]
  );
  const selectedManagerName = selectedManager
    ? `${selectedManager.firstName} ${selectedManager.lastName}`
    : '';
  const isLocationTotalScope = !selectedManagerId;
  const scopeLabel = isLocationTotalScope
    ? `${resolvedLocationCode || context.locationCode || locationLabel} Total / Location Total`
    : `Manager · ${selectedManagerName}`;
  const periodScopeLabel = getPeriodScopeLabel(context);

  const filteredActuals = useMemo(() => actuals.filter((actual) => {
      if (isLocationTotalScope && actual.actualScope !== 'location') {
        return false;
      }
      if (!isLocationTotalScope && String(actual.managerId) !== String(selectedManagerId)) {
        return false;
      }
      if (actual.actualPeriodType !== context.periodType) {
        return false;
      }
      if (normalizePeriodStartValue(actual.periodStart) !== normalizePeriodStartValue(periodStart)) {
        return false;
      }
      return true;
    }), [actuals, context.periodType, isLocationTotalScope, periodStart, selectedManagerId]);

  const allRows = useMemo(() => {
    if (!resolvedLocationCode) {
      return [];
    }

    const rows = buildActualScorecardRows({
      kpis,
      goals,
      actuals: filteredActuals,
      trackerMetrics: [],
      managerName: selectedManagerName,
      context: {
        ...context,
        locationCode: resolvedLocationCode,
        periodStart
      }
    });
    const searchValue = search.trim().toLowerCase();

    if (!searchValue) {
      return rows;
    }

    return rows.filter((row) => (
      row.category.toLowerCase().includes(searchValue)
      || row.goalName.toLowerCase().includes(searchValue)
      || row.kpiKey.toLowerCase().includes(searchValue)
      || row.kpiLabel.toLowerCase().includes(searchValue)
    ));
  }, [context, filteredActuals, goals, kpis, periodStart, resolvedLocationCode, search, selectedManagerName]);

  const visibleGoalIds = useMemo(
    () => [...new Set(
      allRows
        .map((row) => row.exactGoalRecord?.id)
        .filter((value) => Number.isFinite(value))
    )],
    [allRows]
  );

  const rows = useMemo(() => allRows.map((row) => {
    const rowAllocations = row.exactGoalRecord ? (allocationsByGoalId[row.exactGoalRecord.id] || []) : [];
    return {
      ...row,
      allocationCount: rowAllocations.length,
      isAllocated: Boolean(rowAllocations.length)
    };
  }), [allRows, allocationsByGoalId]);

  const statusCounts = useMemo(
    () => rows.reduce((counts, row) => {
      if (!Number.isFinite(row.target)) {
        counts['Missing Target'] += 1;
      } else if (!row.isAllocated) {
        counts['Target Set'] += 1;
      } else if (!row.actualRecord) {
        counts['Awaiting Actual'] += 1;
      } else {
        counts['Actual Entered'] += 1;
      }

      if (row.isAllocated) {
        counts.Allocated += 1;
      }

      return counts;
    }, {
      'Missing Target': 0,
      'Target Set': 0,
      Allocated: 0,
      'Awaiting Actual': 0,
      'Actual Entered': 0
    }),
    [rows]
  );

  const allocationRegister = useMemo(
    () => visibleGoalIds.flatMap((goalId) => allocationsByGoalId[goalId] || []),
    [allocationsByGoalId, visibleGoalIds]
  );

  const buildGoalSnapshot = useCallback((row) => {
    const existingGoal = row.exactGoalRecord || null;
    const draftValue = rowAction.type === 'goal' && rowAction.row?.kpiKey === row.kpiKey
      ? goalDraftValue
      : (Number.isFinite(row.target) ? String(row.target) : '');
    const normalizedDraft = draftValue === '' ? null : Number(draftValue);
    const isDirty = draftValue !== '' && draftValue !== String(Number.isFinite(row.target) ? row.target : '');
    const status = getPerformanceRowStatus('goals', row, { isDirty });

    return {
      stage: 'goals',
      kpiKey: row.kpiKey,
      kpiLabel: row.kpiLabel,
      category: row.category,
      goalName: row.goalName,
      statusLabel: status.label,
      statusTone: status.tone,
      statusDetail: status.detail,
      target: Number.isFinite(normalizedDraft) ? normalizedDraft : row.target,
      actual: row.actual,
      variance: row.variance,
      unit: row.unit,
      formatType: row.formatType,
      rowStateLabel: existingGoal?.isLocked ? 'Locked' : (isDirty ? 'Unsaved Change' : 'Open')
    };
  }, [goalDraftValue, rowAction]);

  const buildAllocationSnapshot = useCallback((row) => {
    const status = getPerformanceRowStatus('allocations', row, {
      isAllocated: row.isAllocated,
      allocationCount: row.allocationCount
    });

    return {
      stage: 'allocations',
      kpiKey: row.kpiKey,
      kpiLabel: row.kpiLabel,
      category: row.category,
      goalName: row.goalName,
      statusLabel: status.label,
      statusTone: status.tone,
      statusDetail: status.detail,
      target: row.targetDisplayValue,
      unit: row.unit,
      formatType: row.formatType,
      allocationCount: row.allocationCount,
      isAllocated: row.isAllocated,
      targetSource: row.targetSource,
      canAllocate: Boolean(row.exactGoalRecord)
    };
  }, []);

  const buildActualSnapshot = useCallback((row) => {
    const draftValue = rowAction.type === 'actual' && rowAction.row?.kpiKey === row.kpiKey
      ? actualDraftValue
      : (Number.isFinite(row.actual) ? String(row.actual) : '');
    const normalizedDraft = draftValue === '' ? null : Number(draftValue);
    const status = getPerformanceRowStatus('actuals', row);

    return {
      stage: 'actuals',
      kpiKey: row.kpiKey,
      kpiLabel: row.kpiLabel,
      category: row.category,
      goalName: row.goalName,
      statusLabel: status.label,
      statusTone: status.tone,
      statusDetail: status.detail,
      target: row.target,
      actual: Number.isFinite(normalizedDraft) ? normalizedDraft : row.actual,
      variance: row.variance,
      unit: row.unit,
      formatType: row.formatType,
      rowStateLabel: row.actualRecord ? 'Saved' : (draftValue !== '' ? 'Unsaved Change' : 'Open')
    };
  }, [actualDraftValue, rowAction]);

  const syncSelectedKpi = useCallback((row) => {
    if (!row) {
      return;
    }

    const goalsSnapshot = buildGoalSnapshot(row);
    const allocationsSnapshot = buildAllocationSnapshot(row);
    const actualsSnapshot = buildActualSnapshot(row);
    const primarySnapshot = defaultStage === 'actuals'
      ? actualsSnapshot
      : defaultStage === 'allocations'
        ? allocationsSnapshot
        : goalsSnapshot;

    onSelectKpi?.(primarySnapshot);
    onUpdateSelectedKpiSnapshot?.(goalsSnapshot);
    onUpdateSelectedKpiSnapshot?.(allocationsSnapshot);
    onUpdateSelectedKpiSnapshot?.(actualsSnapshot);
  }, [
    buildActualSnapshot,
    buildAllocationSnapshot,
    buildGoalSnapshot,
    defaultStage,
    onSelectKpi,
    onUpdateSelectedKpiSnapshot
  ]);

  const loadWorkspaceData = useCallback(async () => {
    const locationCode = resolvedLocationCode;
    const goalParams = new URLSearchParams();

    if (locationCode) {
      goalParams.set('locationCode', locationCode);
    }
    if (context.periodType) {
      goalParams.set('periodType', context.periodType);
    }
    if (periodStart) {
      goalParams.set('periodStart', periodStart);
    }

    const scopedQuery = goalParams.toString() ? `?${goalParams.toString()}` : '';
    const actualParams = new URLSearchParams(goalParams);
    if (selectedManagerId) {
      actualParams.set('managerId', String(selectedManagerId));
    } else {
      actualParams.set('scope', 'location');
    }

    const [goalRows, managerRows, locationRows, kpiRows, actualRows] = await Promise.all([
      apiRequest(`/api/goals/location${scopedQuery}`),
      apiRequest(`/api/managers${locationCode ? `?locationCode=${encodeURIComponent(locationCode)}` : ''}`),
      apiRequest('/api/locations'),
      apiRequest('/api/kpis'),
      apiRequest(`/api/actuals${actualParams.toString() ? `?${actualParams.toString()}` : ''}`)
    ]);

    setGoals(sortGoals(goalRows));
    setManagers(managerRows);
    setLocations(locationRows);
    setKpis(kpiRows);
    setActuals(sortActuals(actualRows));
  }, [context.periodType, periodStart, resolvedLocationCode, selectedManagerId]);

  const loadAllocations = useCallback(async () => {
    if (!visibleGoalIds.length) {
      setAllocationsByGoalId({});
      return;
    }

    const entries = await Promise.all(
      visibleGoalIds.map(async (goalId) => [goalId, await apiRequest(`/api/goals/location/${goalId}/allocations`)])
    );
    setAllocationsByGoalId(Object.fromEntries(entries));
  }, [visibleGoalIds]);

  useEffect(() => {
    let isMounted = true;

    const refresh = async () => {
      if (isMounted) {
        setIsLoading(true);
      }
      try {
        await loadWorkspaceData();
        if (isMounted) {
          setError('');
        }
      } catch (requestError) {
        if (isMounted) {
          setError(requestError.message || 'Unable to load goals and performance workspace.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    refresh();

    const handleDirectorDataUpdated = () => refresh();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
    return () => {
      isMounted = false;
      window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
    };
  }, [loadWorkspaceData]);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        await loadAllocations();
      } catch (requestError) {
        if (isMounted) {
          setError(requestError.message || 'Unable to load allocations.');
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [loadAllocations]);

  useEffect(() => {
    if (!effectiveLocationCode) {
      return;
    }

    if (context.locationCode !== effectiveLocationCode) {
      onPerformanceContextChange?.('locationCode', effectiveLocationCode);
    }
  }, [context.locationCode, effectiveLocationCode, onPerformanceContextChange]);

  useEffect(() => {
    if (!effectiveLocationCode && !context.locationCode && scopedLocations[0]?.code) {
      onPerformanceContextChange?.('locationCode', scopedLocations[0].code);
    }
  }, [context.locationCode, effectiveLocationCode, onPerformanceContextChange, scopedLocations]);

  useEffect(() => {
    if (
      selectedManagerIdExternal
      && scopedManagers.some((manager) => String(manager.id) === String(selectedManagerIdExternal))
      && String(selectedManagerId) !== String(selectedManagerIdExternal)
    ) {
      setSelectedManagerId(String(selectedManagerIdExternal));
    }
  }, [scopedManagers, selectedManagerId, selectedManagerIdExternal]);

  useEffect(() => {
    if (selectedManagerId && !scopedManagers.some((manager) => String(manager.id) === String(selectedManagerId))) {
      setSelectedManagerId('');
      onSelectedManagerChange?.('');
    }
  }, [onSelectedManagerChange, scopedManagers, selectedManagerId]);

  useEffect(() => {
    if (!selectedKpiKey) {
      return;
    }

    const selectedRow = rows.find((row) => row.kpiKey === selectedKpiKey);
    if (selectedRow) {
      syncSelectedKpi(selectedRow);
    }
  }, [rows, selectedKpiKey, syncSelectedKpi]);

  useEffect(() => {
    if (!rowAction.row) {
      setGoalHistory([]);
      setActualHistory([]);
      return;
    }

    if (rowAction.type === 'goal') {
      const target = rowAction.row.target;
      setGoalDraftValue(Number.isFinite(target) ? String(target) : '');
      setAllocationDraft(
        rowAction.row.exactGoalRecord
          ? createAllocationDraftFromRows(allocationsByGoalId[rowAction.row.exactGoalRecord.id] || [])
          : { ...EMPTY_ALLOCATION_DRAFT }
      );
      setActualDraftValue(Number.isFinite(rowAction.row.actual) ? String(rowAction.row.actual) : '');

      if (rowAction.row.exactGoalRecord?.id) {
        apiRequest(`/api/goals/location/${rowAction.row.exactGoalRecord.id}/history`)
          .then((rowsPayload) => setGoalHistory(rowsPayload))
          .catch(() => setGoalHistory([]));
      } else {
        setGoalHistory([]);
      }
      return;
    }

    if (rowAction.type === 'allocation') {
      setAllocationDraft(
        rowAction.row.exactGoalRecord
          ? createAllocationDraftFromRows(allocationsByGoalId[rowAction.row.exactGoalRecord.id] || [])
          : { ...EMPTY_ALLOCATION_DRAFT }
      );
      return;
    }

    if (rowAction.type === 'actual') {
      setActualDraftValue(Number.isFinite(rowAction.row.actual) ? String(rowAction.row.actual) : '');
      if (rowAction.row.actualRecord?.id) {
        apiRequest(`/api/actuals/${rowAction.row.actualRecord.id}/history${rowAction.row.actualRecord.actualScope === 'location' ? '?scope=location' : ''}`)
          .then((rowsPayload) => setActualHistory(rowsPayload))
          .catch(() => setActualHistory([]));
      } else {
        setActualHistory([]);
      }
    }
  }, [allocationsByGoalId, rowAction]);

  const handleContextChange = (field, value) => {
    onPerformanceContextChange?.(field, value);
  };

  const handleOpenAction = (type, row) => {
    syncSelectedKpi(row);
    setRowAction({ type, row });
    setBanner('');
    setError('');
    setActualModalError('');
  };

  const handleSaveGoal = async () => {
    if (!rowAction.row) {
      return;
    }

    const locationCode = resolvedLocationCode;
    const nextTarget = Number(goalDraftValue);
    if (!locationCode || !Number.isFinite(nextTarget)) {
      setError('Select a valid location and enter a numeric goal value.');
      return;
    }

    setIsSaving(true);
    setBanner('');
    setError('');

    try {
      const existingGoal = rowAction.row.exactGoalRecord || null;
      const payload = {
        locationCode,
        kpiKey: rowAction.row.kpiKey,
        goalPeriodType: context.periodType,
        goalYear: Number(context.year),
        goalMonth: context.periodType === 'monthly' ? Number(context.month) : null,
        goalQuarter: context.periodType === 'quarterly' ? Number(context.quarter) : null,
        goalWeek: context.periodType === 'weekly' ? Number(context.week) : null,
        periodStart,
        goalValue: nextTarget
      };
      const savedGoal = await apiRequest(existingGoal ? `/api/goals/location/${existingGoal.id}` : '/api/goals/location', {
        method: existingGoal ? 'PATCH' : 'POST',
        body: JSON.stringify(payload)
      });

      setGoals((current) => sortGoals(
        existingGoal
          ? current.map((goal) => (goal.id === savedGoal.id ? savedGoal : goal))
          : [savedGoal, ...current]
      ));
      setBanner(existingGoal
        ? `Updated goal for ${savedGoal.kpiLabel || rowAction.row.kpiLabel}.`
        : `Created goal for ${savedGoal.kpiLabel || rowAction.row.kpiLabel}.`);
      notifyDirectorAdminToast(existingGoal
        ? `Updated goal for ${rowAction.row.kpiLabel}.`
        : `Created goal for ${rowAction.row.kpiLabel}.`);
      notifyDirectorDataUpdated('Director dashboard refreshed for goal changes.');
      setRowAction(EMPTY_ROW_ACTION);
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to save goal.', 'error');
      setError(requestError.message || 'Unable to save goal.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAllocation = async () => {
    if (!rowAction.row?.exactGoalRecord?.id) {
      setError('Create an exact target before saving allocations.');
      return;
    }

    if (!allocationDraft.allocationType) {
      setError('Select an allocation method before saving.');
      return;
    }

    if (allocationDraft.allocationType === 'individual_assignment' && allocationDraft.managerIds.length !== 1) {
      setError('Select exactly one manager for a single-manager allocation.');
      return;
    }

    if (allocationDraft.allocationType === 'group_assignment' && allocationDraft.managerIds.length < 1) {
      setError('Select at least one manager for a group allocation.');
      return;
    }

    setIsSaving(true);
    setBanner('');
    setError('');

    try {
      const result = await apiRequest(`/api/goals/location/${rowAction.row.exactGoalRecord.id}/allocate`, {
        method: 'POST',
        body: JSON.stringify({
          allocationType: allocationDraft.allocationType,
          managerIds: allocationDraft.allocationType === 'equal_split' ? [] : allocationDraft.managerIds
        })
      });

      setAllocationsByGoalId((current) => ({
        ...current,
        [rowAction.row.exactGoalRecord.id]: result.allocations
      }));
      setBanner(`Saved ${result.allocations.length} allocation row(s) for ${rowAction.row.kpiLabel}.`);
      notifyDirectorAdminToast(`Saved allocation for ${rowAction.row.kpiLabel}.`);
      notifyDirectorDataUpdated('Director dashboard refreshed for allocation changes.');
      setRowAction(EMPTY_ROW_ACTION);
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to save allocation.', 'error');
      setError(requestError.message || 'Unable to save allocation.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveActual = async () => {
    if (!rowAction.row) {
      return;
    }

    if (!Number.isFinite(rowAction.row.target)) {
      setActualModalError('A goal or allocation is required before recording an actual.');
      return;
    }

    const nextActual = Number(actualDraftValue);
    if (!Number.isFinite(nextActual)) {
      setActualModalError('Enter a numeric actual value.');
      return;
    }

    setIsSaving(true);
    setBanner('');
    setError('');
    setActualModalError('');

    try {
      const payload = {
        ...(selectedManagerId ? { managerId: Number(selectedManagerId) } : { actualScope: 'location' }),
        kpiDefinitionId: Number(rowAction.row.kpiDefinitionId),
        actualPeriodType: context.periodType,
        actualYear: Number(context.year),
        actualMonth: context.periodType === 'monthly' ? Number(context.month) : null,
        actualQuarter: context.periodType === 'quarterly' ? Number(context.quarter) : null,
        actualWeek: context.periodType === 'weekly' ? Number(context.week) : null,
        periodStart,
        actualDate: periodStart,
        actualValue: nextActual,
        sourceType: 'manual',
        locationCode: resolvedLocationCode
      };
      const existingActual = rowAction.row.actualRecord || null;
      const savedActual = await apiRequest(
        existingActual
          ? `/api/actuals/${existingActual.id}${isLocationTotalScope ? '?scope=location' : ''}`
          : '/api/actuals',
        {
        method: existingActual ? 'PATCH' : 'POST',
        body: JSON.stringify(payload)
        }
      );

      setActuals((current) => sortActuals(
        existingActual
          ? current.map((actual) => (actual.id === savedActual.id ? savedActual : actual))
          : [savedActual, ...current]
      ));
      // Re-read the scoped workspace rows from the canonical actuals endpoint.
      // Mutation responses can be intentionally compact, so relying on them
      // alone leaves the current tab stale even though downstream tabs refresh.
      await loadWorkspaceData();
      setBanner(existingActual
        ? `Updated actual for ${rowAction.row.kpiLabel}.`
        : `Saved actual for ${rowAction.row.kpiLabel}.`);
      notifyDirectorAdminToast(existingActual
        ? `Updated actual for ${rowAction.row.kpiLabel}.`
        : `Saved actual for ${rowAction.row.kpiLabel}.`);
      notifyDirectorDataUpdated('Director dashboard refreshed for actual changes.');
      setRowAction(EMPTY_ROW_ACTION);
    } catch (requestError) {
      if (process.env.NODE_ENV !== 'production') {
        // Keep request context local-only so production logs never collect user-entered KPI values.
        console.warn('Goals & Performance actual save failed.', {
          endpoint: rowAction.row.actualRecord ? `/api/actuals/${rowAction.row.actualRecord.id}` : '/api/actuals',
          managerId: selectedManagerId ? Number(selectedManagerId) : null,
          kpiDefinitionId: Number(rowAction.row.kpiDefinitionId),
          periodType: context.periodType,
          periodStart,
          locationCode: resolvedLocationCode,
          status: requestError?.status,
          message: requestError?.message
        });
      }
      notifyDirectorAdminToast(requestError.message || 'Unable to save actual.', 'error');
      setActualModalError(requestError.message || 'Unable to save actual.');
    } finally {
      setIsSaving(false);
    }
  };

  const getEligibleManagersForRow = useCallback((row) => (
    scopedManagers.filter((manager) => (
      !isGlobalDirectorManager(manager)
      && manager.locationCode === (row.locationCode || resolvedLocationCode)
    ))
  ), [resolvedLocationCode, scopedManagers]);

  const closeRowAction = () => {
    setActualModalError('');
    setRowAction(EMPTY_ROW_ACTION);
  };

  return (
    <div className="director-admin-stack">
      <DirectorPanelState kind="loading" message={isLoading ? 'Loading goals and performance workspace...' : ''} />
      <DirectorPanelState kind="error" message={error} />
      <DirectorPanelState kind="success" message={!error ? banner : ''} />

      <PerformanceContextBar
        title={workspaceCopy.title}
        context={context}
        onChange={handleContextChange}
        locations={scopedLocations}
        selectedLocationCode={selectedLocationCode}
        allLocationsValue={ALL_DIRECTOR_LOCATIONS}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search category, goal, or KPI"
      >
        <label className="director-toolbar-group">
          Manager
          <select
            value={selectedManagerId}
            onChange={(event) => {
              const nextManagerId = event.target.value;
              setSelectedManagerId(nextManagerId);
              onSelectedManagerChange?.(nextManagerId);
            }}
          >
            <option value="">PHX Total / Location Total</option>
            {scopedManagers.map((manager) => (
              <option key={manager.id} value={manager.id}>
                {manager.firstName} {manager.lastName} ({manager.locationCode})
              </option>
            ))}
          </select>
        </label>
      </PerformanceContextBar>

      <PerformanceStatusSummaryStrip counts={statusCounts} />

      <section className="director-card director-scorecard-surface">
        <div className="role-section-header">
          <h2>{workspaceCopy.title}</h2>
          <span>{rows.length} KPI rows</span>
        </div>
        <p className="role-section-copy">{workspaceCopy.description}</p>
        <div className="director-scorecard-meta">
          <span className="director-scorecard-meta-pill">Location: {resolvedLocationCode || context.locationCode || locationLabel}</span>
          <span className="director-scorecard-meta-pill">Period: {periodScopeLabel}</span>
          <span className="director-scorecard-meta-pill">Scope: {scopeLabel}</span>
          <span className="director-scorecard-meta-pill">Period Start: {periodStart || 'Select a valid period'}</span>
        </div>
        <DataTable
          tableClassName="director-scorecard-table director-goals-performance-table"
          aria-label={`${workspaceCopy.title} table, scroll horizontally for more columns`}
          emptyMessage="No KPI rows match the current goals and performance context."
          isEmpty={!rows.length}
          columns={[
            { label: 'KPI' },
            { label: workspaceCopy.targetColumn },
            { label: 'Allocations' },
            { label: workspaceCopy.actualColumn },
            { label: 'Variance (+/-)' },
            { label: 'Status' },
            { label: 'Actions' }
          ]}
        >
          {rows.map((row) => {
            const goalStatus = getPerformanceRowStatus('goals', row);
            const allocationStatus = getPerformanceRowStatus('allocations', row, {
              isAllocated: row.isAllocated,
              allocationCount: row.allocationCount
            });
            const actualStatus = getPerformanceRowStatus('actuals', row);

            return (
              <tr
                key={row.kpiKey}
                className={selectedKpiKey === row.kpiKey ? 'director-kpi-row-focused' : ''}
                onClick={() => syncSelectedKpi(row)}
              >
                <td>
                  <strong>{row.kpiLabel}</strong>
                  <div className="director-muted">{row.kpiKey}</div>
                  <div className="director-muted">{row.category} / {row.goalName}</div>
                </td>
                <td>
                  {Number.isFinite(row.target)
                    ? <strong>{formatMetricValue(row.target, row.unit, row.formatType)}</strong>
                    : <span className="director-warning-copy director-inline-note">No target set</span>}
                  {row.targetSource === 'yearly_reference' ? <div className="director-muted">Using yearly reference</div> : null}
                </td>
                <td>
                  <div className="director-target-meta">
                    <StatusPill tone={allocationStatus.tone}>{allocationStatus.label}</StatusPill>
                    <span className="director-muted">
                      {row.allocationCount
                        ? `${row.allocationCount} manager assignment${row.allocationCount === 1 ? '' : 's'}`
                        : 'No allocations saved'}
                    </span>
                  </div>
                </td>
                <td>
                  {Number.isFinite(row.actual)
                    ? <strong>{formatMetricValue(row.actual, row.unit, row.formatType)}</strong>
                    : <span className="director-warning-copy director-inline-note">No actual recorded</span>}
                </td>
                <td>{formatMetricValue(row.variance, row.unit, row.formatType)}</td>
                <td>
                  <div className="director-row-status director-goals-performance-status-stack">
                    <StatusPill tone={goalStatus.tone}>Goal: {goalStatus.label}</StatusPill>
                    <StatusPill tone={allocationStatus.tone}>Alloc: {allocationStatus.label}</StatusPill>
                    <StatusPill tone={actualStatus.tone}>Actual: {actualStatus.label}</StatusPill>
                  </div>
                </td>
                <td>
                  <div className="director-table-actions director-goals-performance-actions">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpenAction('goal', row);
                      }}
                    >
                      {row.exactGoalRecord ? 'Edit Goal' : 'Add Goal'}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpenAction('allocation', row);
                      }}
                    >
                      {row.isAllocated ? 'Edit Allocation' : 'Allocate'}
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpenAction('actual', row);
                      }}
                    >
                      {row.actualRecord ? 'Edit Actual' : 'Record Actual'}
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </DataTable>
      </section>

      <details className="director-card director-collapsible-card director-secondary-card">
        <summary className="director-collapsible-summary">
          <h2>Current Allocations</h2>
          <span>{allocationRegister.length} rows</span>
        </summary>
        <div className="director-collapsible-body">
          <DataTable
            tableClassName="director-current-allocations-table"
            aria-label="Current Allocations table, scroll horizontally for more columns"
            emptyMessage="No allocations have been saved for the current context yet."
            isEmpty={!allocationRegister.length}
            columns={[
              { label: 'Manager' },
              { label: 'KPI' },
              { label: 'Allocated Goal' },
              { label: 'Type' },
              { label: 'Allocated At' }
            ]}
          >
            {allocationRegister.map((allocation) => (
              <tr key={allocation.id}>
                <td>{allocation.managerName}</td>
                <td>{allocation.kpiLabel}</td>
                <td>{allocation.allocatedValue}</td>
                <td>{allocation.allocationType}</td>
                <td>{formatAllocationTimestamp(allocation.allocatedAt)}</td>
              </tr>
            ))}
          </DataTable>
        </div>
      </details>

      {rowAction.type === 'goal' && rowAction.row ? (
        <DirectorModal
          title={rowAction.row.exactGoalRecord ? 'Edit Goal' : 'Add Goal'}
          badge="Goals"
          onClose={closeRowAction}
        >
          <div className="director-history-row">
            <strong>{rowAction.row.kpiLabel}</strong>
            <span>{context.locationCode || locationLabel} / {rowAction.row.goalName}</span>
          </div>
          <p className="director-muted">
            Edit the location goal in-place without leaving the scorecard. This reuses the existing goal record and history workflow.
          </p>
          <div className="director-form-grid director-inline-form-grid">
            <label>
              KPI
              <input type="text" value={`${rowAction.row.kpiLabel} (${rowAction.row.kpiKey})`} readOnly />
            </label>
            <label>
              Goal Value
              <input
                className="director-target-input"
                type="number"
                step="0.01"
                value={goalDraftValue}
                onChange={(event) => setGoalDraftValue(event.target.value)}
                disabled={rowAction.row.exactGoalRecord?.isLocked}
              />
            </label>
            <label>
              Current Actual
              <input
                type="text"
                value={Number.isFinite(rowAction.row.actual)
                  ? formatMetricValue(rowAction.row.actual, rowAction.row.unit, rowAction.row.formatType)
                  : 'No actual recorded'}
                readOnly
              />
            </label>
          </div>
          {goalHistory.length ? (
            <div className="director-history-list">
              {goalHistory.slice(0, 5).map((item) => (
                <div className="director-history-item" key={item.id}>
                  <div className="director-history-row">
                    <strong>{item.action}</strong>
                    <span>{new Date(item.changedAt).toLocaleString()}</span>
                  </div>
                  <div className="director-muted">
                    Value: {item.oldValue ?? 'n/a'} -&gt; {item.newValue ?? 'n/a'}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          <div className="director-form-actions director-form-actions-spread">
            <Button
              variant="primary"
              onClick={handleSaveGoal}
              disabled={rowAction.row.exactGoalRecord?.isLocked}
              loading={isSaving}
              loadingLabel="Saving…"
            >
              {rowAction.row.exactGoalRecord ? 'Save Goal' : 'Create Goal'}
            </Button>
            <Button size="sm" variant="secondary" onClick={closeRowAction} disabled={isSaving}>
              Close
            </Button>
          </div>
        </DirectorModal>
      ) : null}

      {rowAction.type === 'allocation' && rowAction.row ? (
        <DirectorModal
          title="Manage Allocation"
          badge="Allocations"
          onClose={closeRowAction}
        >
          <div className="director-history-row">
            <strong>{rowAction.row.kpiLabel}</strong>
            <span>{context.locationCode || locationLabel} / {rowAction.row.goalName}</span>
          </div>
          {!rowAction.row.exactGoalRecord ? (
            <>
              <p className="director-warning-copy director-inline-note">
                Create an exact goal for this period before assigning allocations.
              </p>
              <div className="director-form-actions">
                <Button
                  variant="primary"
                  onClick={() => setRowAction({ type: 'goal', row: rowAction.row })}
                >
                  Open Goal Editor
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="director-form-grid director-inline-form-grid">
                <label>
                  Target
                  <input
                    type="text"
                    value={formatMetricValue(rowAction.row.targetDisplayValue, rowAction.row.unit, rowAction.row.formatType)}
                    readOnly
                  />
                </label>
                <label>
                  Allocation Method
                  <select
                    value={allocationDraft.allocationType}
                    onChange={(event) => setAllocationDraft({
                      allocationType: event.target.value,
                      managerIds: []
                    })}
                    disabled={rowAction.row.exactGoalRecord.isLocked}
                  >
                    <option value="">Select allocation method</option>
                    <option value="equal_split">Equal split across active managers</option>
                    <option value="individual_assignment">Single manager</option>
                    <option value="group_assignment">Selected group</option>
                  </select>
                </label>
              </div>

              {allocationDraft.allocationType === 'individual_assignment' ? (
                <label className="director-toolbar-group">
                  Manager
                  <select
                    value={allocationDraft.managerIds[0] || ''}
                    onChange={(event) => setAllocationDraft((current) => ({
                      ...current,
                      managerIds: event.target.value ? [Number(event.target.value)] : []
                    }))}
                    disabled={rowAction.row.exactGoalRecord.isLocked}
                  >
                    <option value="">Select manager</option>
                    {getEligibleManagersForRow(rowAction.row).map((manager) => (
                      <option key={manager.id} value={manager.id}>
                        {manager.firstName} {manager.lastName} ({manager.roleTitle})
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {allocationDraft.allocationType === 'group_assignment' ? (
                <label className="director-toolbar-group">
                  Managers
                  <select
                    multiple
                    value={allocationDraft.managerIds.map(String)}
                    onChange={(event) => setAllocationDraft((current) => ({
                      ...current,
                      managerIds: Array.from(event.target.selectedOptions, (option) => Number(option.value))
                    }))}
                    disabled={rowAction.row.exactGoalRecord.isLocked}
                  >
                    {getEligibleManagersForRow(rowAction.row).map((manager) => (
                      <option key={manager.id} value={manager.id}>
                        {manager.firstName} {manager.lastName} ({manager.roleTitle})
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {rowAction.row.isAllocated ? (
                <div className="director-history-list">
                  {(allocationsByGoalId[rowAction.row.exactGoalRecord.id] || []).map((allocation) => (
                    <div className="director-history-item" key={allocation.id}>
                      <div className="director-history-row">
                        <strong>{allocation.managerName}</strong>
                        <span>{allocation.allocationType}</span>
                      </div>
                      <div className="director-muted">
                        Allocated value: {allocation.allocatedValue} / Saved {formatAllocationTimestamp(allocation.allocatedAt)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="director-form-actions director-form-actions-spread">
                <Button
                  variant="primary"
                  onClick={handleSaveAllocation}
                  disabled={rowAction.row.exactGoalRecord.isLocked}
                  loading={isSaving}
                  loadingLabel="Saving…"
                >
                  Save Allocation
                </Button>
                <Button size="sm" variant="secondary" onClick={closeRowAction} disabled={isSaving}>
                  Close
                </Button>
              </div>
            </>
          )}
        </DirectorModal>
      ) : null}

      {rowAction.type === 'actual' && rowAction.row ? (
        <DirectorModal
          title={rowAction.row.actualRecord ? 'Edit Actual' : 'Record Actual'}
          badge="Actuals"
          onClose={closeRowAction}
        >
          <div className="director-history-row">
            <strong>{selectedManagerName ? `Recording actual for ${selectedManagerName}` : `Recording actual for ${resolvedLocationCode || context.locationCode || locationLabel} Total`}</strong>
            <span>{rowAction.row.kpiLabel}</span>
          </div>
          <div className="director-scorecard-meta">
            <span className="director-scorecard-meta-pill">Location: {resolvedLocationCode || context.locationCode || locationLabel}</span>
            <span className="director-scorecard-meta-pill">Period: {periodScopeLabel}</span>
            <span className="director-scorecard-meta-pill">KPI: {rowAction.row.kpiLabel}</span>
          </div>
          <>
              {actualModalError ? (
                <StatusBanner kind="error" message={actualModalError} />
              ) : null}
              <div className="director-form-grid director-inline-form-grid">
                <label>
                  Goal
                  <input
                    type="text"
                    value={Number.isFinite(rowAction.row.target)
                      ? formatMetricValue(rowAction.row.target, rowAction.row.unit, rowAction.row.formatType)
                      : 'No goal or allocation available'}
                    readOnly
                  />
                </label>
                <label>
                  Actual Value
                  <input
                    className="director-target-input"
                    type="number"
                    step="0.01"
                    value={actualDraftValue}
                    onChange={(event) => setActualDraftValue(event.target.value)}
                    disabled={rowAction.row.actualRecord?.isLocked}
                  />
                </label>
                <label>
                  Variance (+/-)
                  <input
                    type="text"
                    value={formatMetricValue(rowAction.row.variance, rowAction.row.unit, rowAction.row.formatType)}
                    readOnly
                  />
                </label>
              </div>
              {actualHistory.length ? (
                <div className="director-history-list">
                  {actualHistory.slice(0, 5).map((item) => (
                    <div className="director-history-item" key={item.id}>
                      <div className="director-history-row">
                        <strong>{item.action}</strong>
                        <span>{new Date(item.changedAt).toLocaleString()}</span>
                      </div>
                      <div className="director-muted">
                        Value: {item.oldValue ?? 'n/a'} -&gt; {item.newValue ?? 'n/a'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
              <div className="director-form-actions director-form-actions-spread">
                <Button
                  variant="primary"
                  onClick={handleSaveActual}
                  disabled={rowAction.row.actualRecord?.isLocked}
                  loading={isSaving}
                  loadingLabel="Saving…"
                >
                  {rowAction.row.actualRecord ? 'Save Actual' : 'Record Actual'}
                </Button>
                <Button size="sm" variant="secondary" onClick={closeRowAction} disabled={isSaving}>
                  Close
                </Button>
              </div>
          </>
        </DirectorModal>
      ) : null}
    </div>
  );
}

export default DirectorGoalsPerformancePanel;
